import type { ReactElement } from "react";
import { Popover } from "antd";

import { AppStrings } from "../../../../utils/constants/AppStrings";
import { formatDateNoTimezone } from "../../../../utils/dateService";
import { formatLargeNumbers } from "../../../../utils/NumberFormatter";
import { formatCurrency } from "../../../../utils/financialFormatters";
import type { Transaction, TransactionStatus } from "../../../../types/transactions";

type StatusDisplayConfig = { label: string; className: string };

export const TRANSACTION_STATUS_DISPLAY: Record<string, StatusDisplayConfig> = {
  pending: { label: AppStrings.STATUS_PENDING, className: "pending" },
  approved: {
    label: AppStrings.STATUS_APPROVED_INITIATED,
    className: "approved",
  },
  initiated: {
    label: AppStrings.STATUS_APPROVED_INITIATED,
    className: "approved",
  },
  "approved/initiated": {
    label: AppStrings.STATUS_APPROVED_INITIATED,
    className: "approved",
  },
  declined: { label: AppStrings.STATUS_DECLINED, className: "declined" },
  // PAMENG-2535: Bitso penny-verification (CLABE verification) statuses.
  verified: { label: AppStrings.PAM_STATUS_VERIFIED, className: "approved" },
  "initiation failed": {
    label: AppStrings.PAM_STATUS_INITIATION_FAILED,
    className: "declined",
  },
};

const isFiatWallet = (walletType: string | null | undefined): boolean =>
  walletType?.trim().toLowerCase() === "cash";

export const formatTransactionMoney = (
  value: string | number | null,
  walletType: string | null | undefined,
  currencySymbol?: string,
): string =>
  isFiatWallet(walletType)
    ? formatCurrency(Number(value ?? 0), currencySymbol, 2)
    : formatLargeNumbers(value ?? 0, 2);

export const renderOptionalValue = (
  value: string | number | null | undefined,
): string | number => {
  if (value === null || value === undefined || value === "") {
    return "N/A";
  }

  return value;
};

export const renderPaymentTypeLabel = (
  record: Pick<Transaction, "payment_type" | "payment_gateway">,
): string | number => {
  const paymentType = record?.payment_type;
  const paymentGateway = record?.payment_gateway;

  if (paymentType && paymentGateway) {
    return `${paymentType} - ${paymentGateway}`;
  }

  return renderOptionalValue(paymentType);
};

export const renderTransactionStatus = (
  status: TransactionStatus | string,
): ReactElement => {
  const normalized =
    typeof status === "string" ? status.trim().toLowerCase() : "";
  const config = TRANSACTION_STATUS_DISPLAY[normalized];

  if (!config) {
    return <p className="status-text dash">—</p>;
  }

  return <p className={`status-text ${config.className}`}>{config.label}</p>;
};

const renderVerificationDetailRow = (
  label: string,
  value: string | number | null | undefined,
): ReactElement => (
  <div className="verification-detail-row">
    <span className="verification-detail-label">{label}</span>
    <span className="verification-detail-value">
      {renderOptionalValue(value)}
    </span>
  </div>
);

// PAMENG-2535: Bitso penny-verification rows carry a `verification_details`
// payload ops needs to inspect. Surfaced via a Popover on Card/Bank Info,
// with a red AML tag when the check was flagged.
export const renderVerificationDetailsCell = (
  text: string | null | undefined,
  record: Pick<Transaction, "verification_details">,
): ReactElement => {
  const details = record?.verification_details;
  const isAmlFlagged = details?.aml_flag === true;
  const formatDetailTimestamp = (
    value: string | null | undefined,
  ): string | null =>
    value ? formatDateNoTimezone(value, "DD MMMM YYYY, HH:mm:ss") : null;

  const content = (
    <div className="verification-details-popover">
      <div className="verification-details-header">
        <span className="verification-details-title">
          {AppStrings.PAM_VERIFICATION_DETAILS}
        </span>
        {isAmlFlagged && <span className="aml-tag">{AppStrings.PAM_AML}</span>}
      </div>
      {renderVerificationDetailRow(AppStrings.PAM_CLABE, details?.clabe)}
      {renderVerificationDetailRow(
        AppStrings.PAM_HOLDER_NAME,
        details?.holder_name,
      )}
      {renderVerificationDetailRow(
        AppStrings.PAM_BITSO_PAYOUT_ID,
        details?.verify_wid,
      )}
      {renderVerificationDetailRow(AppStrings.REASON, details?.reason)}
      {renderVerificationDetailRow(
        AppStrings.PAM_AML_FLAG,
        details?.aml_flag == null
          ? null
          : isAmlFlagged
            ? AppStrings.YES
            : AppStrings.NO,
      )}
      {renderVerificationDetailRow(
        AppStrings.CREATED_AT,
        formatDetailTimestamp(details?.created_at),
      )}
      {renderVerificationDetailRow(
        AppStrings.UPDATED_AT,
        formatDetailTimestamp(details?.updated_at),
      )}
    </div>
  );

  return (
    <Popover content={content} placement="topLeft">
      <span
        className="verification-details-cell"
        data-testid="verification-details-cell"
      >
        <p className="reason-text">{renderOptionalValue(text)}</p>
        {isAmlFlagged && (
          <span className="aml-tag" data-testid="verification-aml-tag">
            {AppStrings.PAM_AML}
          </span>
        )}
      </span>
    </Popover>
  );
};

export const renderCardBankInfoCell = (
  text: string | null | undefined,
  record: Pick<Transaction, "is_verification" | "verification_details">,
): ReactElement | string | number =>
  record?.is_verification
    ? renderVerificationDetailsCell(text, record)
    : renderOptionalValue(text);

export const renderReasonCell = (
  text: string | null | undefined,
  record: Pick<Transaction, "by_admin" | "decline_reason">,
): ReactElement => {
  // PAMENG-2535: verification rows send their reason token as
  // `decline_reason` (top-level), not `reason`.
  const reasonText = text || record?.decline_reason;
  if (record?.by_admin || reasonText) {
    return (
      <Popover content={reasonText} placement="topLeft">
        <p className="reason-text ellipsis">{reasonText || "-"}</p>
      </Popover>
    );
  }
  return <p className="reason-text ellipsis">-</p>;
};

export const formatTransactionBalance = (
  text: string | number | null,
  record: Pick<Transaction, "is_verification" | "wallet_type">,
  currencySymbol?: string,
): string =>
  // PAMENG-2535: verification rows have no balance impact and send
  // total_balance: null — show a dash rather than a misleading 0.00.
  record?.is_verification && text == null
    ? "—"
    : formatTransactionMoney(text, record.wallet_type, currencySymbol);
